import { Controller } from '@hotwired/stimulus';

/**
 * Casquette tag-input controller — the create/edit intervenant form's
 * « Qualité(s) sur ce chantier » manager (#556 feedback wave A / F1). The sole
 * type control: a compact select adds a casquette (role) as a removable chip;
 * the FIRST chip (insertion order) carries a ★ marking the server-derived
 * `kind`. At least one casquette is required client-side (removal is blocked
 * when it would empty the set) — the server re-validates independently
 * (`MeetingReports::CarnetIntervenantsController#create_intervenant`), never
 * rely on JS alone.
 *
 * Targets:
 * - select: the « + Ajouter une qualité… » <select> (REQUIRED).
 * - chips: the chip container new chips are appended to / removed from
 *   (REQUIRED). Server-rendered chips (422 re-render) already live inside it.
 * - iconDictionary: hidden container holding the server-rendered <template>
 *   icons (× remove / ★ default) a newly-built chip clones from (REQUIRED —
 *   DESIGN.md contract #18, no JS-authored <svg>; mirrors gantt_controller.js's
 *   #cloneIcon idiom).
 *
 * Values:
 * - chipLabelMap (Object): role → FR label (e.g. {"bet": "BET", "opc": "OPC"}),
 *   in Intervenant::KINDS order — drives the label on a newly added chip and
 *   the restore position of a removed casquette's <option>.
 * - fieldName (String): the hidden input `name=` for every chip (e.g.
 *   "project_directory_entry[casquettes][]").
 * - starLabel (String, default 'Qualité par défaut'): unused visually today,
 *   reserved for a future aria-label on the ★ (kept for API stability).
 * - removeLabelPrefix (String, default 'Retirer la qualité '): prefix for a
 *   newly-built chip's remove button aria-label ("<prefix><label>").
 *
 * ⚠️ Ces deux défauts sont de la COPIE VISIBLE et doivent rester alignés sur
 * `_casquette_manager.html.erb`, qui les redescend en `data-*-value`. Le
 * vocabulaire produit est « Qualité » (`scripts/lint-terminology.sh`) ; seul le
 * nom du contrôleur reste « casquette » (terme interne, ADR-013). Aucun lint ne
 * lit les `.js` : un défaut désaligné ferait porter à une puce ajoutée côté
 * client un autre mot qu'à une puce rendue au serveur, en silence.
 *
 * @example
 *   <div data-controller="casquette-tags"
 *        data-casquette-tags-chip-label-map-value='{"bet":"BET","opc":"OPC"}'
 *        data-casquette-tags-field-name-value="project_directory_entry[casquettes][]">
 *     <div data-casquette-tags-target="chips" class="contents">…SSR chips…</div>
 *     <select data-casquette-tags-target="select" data-action="change->casquette-tags#add">…</select>
 *   </div>
 */
export default class extends Controller {
  static targets = ['select', 'chips', 'iconDictionary'];

  static values = {
    chipLabelMap: Object,
    fieldName: String,
    starLabel: { type: String, default: 'Qualité par défaut' },
    removeLabelPrefix: { type: String, default: 'Retirer la qualité ' },
  };

  connect() {
    // Defensive re-sync (SSR already places the ★ correctly on a fresh render;
    // this is a no-op then, but protects a future caller that mounts the
    // controller on a pre-built DOM without the star).
    this.#refreshStar();
  }

  // Adds the selected role as a chip, removes the option from the select, and
  // resets the select to its placeholder.
  add() {
    const select = this.selectTarget;
    const role = select.value;
    if (!role) return;

    this.chipsTarget.appendChild(this.#buildChip(role));

    const option = select.querySelector(`option[value="${CSS.escape(role)}"]`);
    if (option) option.remove();
    select.value = '';

    this.#refreshStar();
  }

  // Removes a chip. Blocked when it is the last remaining casquette — at least
  // one is always required (the server re-validates independently).
  remove(event) {
    if (this.chipsTarget.children.length <= 1) return;

    const wrapper = event.currentTarget.closest('[data-casquette-chip-role]');
    if (!wrapper) return;

    const role = wrapper.dataset.casquetteChipRole;
    wrapper.remove();
    this.#restoreOption(role);
    this.#refreshStar();
  }

  // ── DOM construction (createElement only — no innerHTML; classes mirror
  //    Ui::ChipComponent / Ui::IconComponent output 1:1; icons are cloned from
  //    the server-rendered iconDictionary <template>s — no JS-authored <svg>,
  //    DESIGN.md contract #18) ──

  #buildChip(role) {
    const label = this.chipLabelMapValue[role] || role;

    const wrapper = document.createElement('span');
    wrapper.className = 'inline-flex items-center gap-1';
    wrapper.dataset.casquetteChipRole = role;

    wrapper.appendChild(this.#buildChipBody(role, label));
    wrapper.appendChild(this.#buildHiddenInput(role));

    return wrapper;
  }

  #buildChipBody(role, label) {
    // Mirrors Ui::ChipComponent(variant: :tag, removable: true) markup.
    const span = document.createElement('span');
    span.className =
      'inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2 py-0.5 text-xs text-neutral-700';
    span.appendChild(document.createTextNode(label));

    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', `${this.removeLabelPrefixValue}${label}`);
    button.className =
      'ml-1 -mr-0.5 inline-flex items-center rounded-sm text-neutral-400 hover:text-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';
    button.dataset.action = 'click->casquette-tags#remove';
    button.dataset.role = role;
    const removeIcon = this.#cloneIcon('remove');
    if (removeIcon) button.appendChild(removeIcon);
    span.appendChild(button);

    return span;
  }

  #buildHiddenInput(role) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = this.fieldNameValue;
    input.value = role;
    return input;
  }

  // Clone an SVG out of the icon dictionary template (never author SVG in
  // JS — DESIGN.md contract #18). Mirrors gantt_controller.js's #cloneIcon.
  #cloneIcon(key) {
    if (!this.hasIconDictionaryTarget) return null;
    const template = this.iconDictionaryTarget.querySelector(
      `template[data-casquette-icon="${key}"]`
    );
    if (!template) return null;
    const fragment = template.content.cloneNode(true);
    return fragment.querySelector('svg');
  }

  // Restores a removed role's <option>, keeping the chipLabelMap's (server —
  // Intervenant::KINDS) order rather than appending at the end.
  #restoreOption(role) {
    const select = this.selectTarget;
    if (select.querySelector(`option[value="${CSS.escape(role)}"]`)) return;

    const option = document.createElement('option');
    option.value = role;
    option.textContent = this.chipLabelMapValue[role] || role;

    const roles = Object.keys(this.chipLabelMapValue);
    const roleIndex = roles.indexOf(role);
    const next = Array.from(select.options).find(
      (opt) => opt.value && roles.indexOf(opt.value) > roleIndex
    );
    select.insertBefore(option, next || null);
  }

  // The FIRST chip (insertion order) carries the ★ — moves it after an add/remove.
  // Detection is structural (direct-child <svg>), not attribute-based, so it
  // matches both the SSR-rendered star and the JS-built one identically.
  #refreshStar() {
    Array.from(this.chipsTarget.children).forEach((wrapper, index) => {
      const existingStar = wrapper.querySelector(':scope > svg');
      if (index === 0 && !existingStar) {
        const star = this.#cloneIcon('star');
        if (star) wrapper.insertBefore(star, wrapper.firstChild);
      } else if (index > 0 && existingStar) {
        existingStar.remove();
      }
    });
  }
}
